import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { CustomSelect } from '@/components/ui/custom-select';
import { 
  BarChart3, TrendingUp, TrendingDown, Activity, Target, 
  Clock, CheckCircle, AlertTriangle, Users, Layers,
  Calendar, Zap, PieChart, Filter
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ScatterChart, Scatter, ReferenceLine, ZAxis } from 'recharts';
import TeamBandwidth from '@/components/TeamBandwidth';

// State colors for intake items
const stateConfig = {
  CAPTURED: { label: 'Captured', color: 'bg-purple-500' },
  INTAKE_COMPLETE: { label: 'Intake Complete', color: 'bg-cyan-500' },
  REFINED: { label: 'Refined', color: 'bg-indigo-500' },
  SIZED: { label: 'Sized', color: 'bg-orange-500' },
  SCORED: { label: 'Scored', color: 'bg-pink-500' },
  PLANNED: { label: 'Planned', color: 'bg-blue-500' },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-emerald-500' },
  DONE: { label: 'Done', color: 'bg-green-500' },
  BLOCKED: { label: 'Blocked', color: 'bg-red-500' },
  DISCARDED: { label: 'Discarded', color: 'bg-gray-400' },
  NEEDS_INFO: { label: 'Needs Info', color: 'bg-yellow-500' },
  NEEDS_REWORK: { label: 'Needs Rework', color: 'bg-amber-500' },
  DEFERRED: { label: 'Deferred', color: 'bg-gray-500' },
};

// Priority colors
const priorityConfig = {
  P0: { label: 'P0 - Critical', color: 'bg-red-500', badge: 'bg-red-100 text-red-700' },
  P1: { label: 'P1 - High', color: 'bg-orange-500', badge: 'bg-orange-100 text-orange-700' },
  P2: { label: 'P2 - Medium', color: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-700' },
  P3: { label: 'P3 - Low', color: 'bg-gray-400', badge: 'bg-gray-100 text-gray-600' },
  P4: { label: 'P4 - Minimal', color: 'bg-gray-300', badge: 'bg-gray-50 text-gray-500' },
};

export default function Metrics() {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [items, setItems] = useState([]);
  const [stories, setStories] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [selectedSprint, setSelectedSprint] = useState('all');
  const [sprintHealth, setSprintHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [burndownData, setBurndownData] = useState(null);
  const [loadingBurndown, setLoadingBurndown] = useState(false);
  const [flowMetrics, setFlowMetrics] = useState(null);
  const [loadingFlow, setLoadingFlow] = useState(false);
  const [controlChartTab, setControlChartTab] = useState('cycle');
  const [prMetrics, setPrMetrics] = useState(null);
  const [loadingPr, setLoadingPr] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [prodRes, sprintRes, itemsRes, storiesRes] = await Promise.all([
          api.get('/products/'),
          api.get('/sprints/'),
          api.get('/intake/items'),
          api.get('/sprints/stories')
        ]);
        
        setProducts(prodRes.data);
        setSprints(sprintRes.data);
        setItems(itemsRes.data);
        setStories(storiesRes.data);
      } catch (err) {
        console.error('Failed to load metrics:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Build story→product mapping via intake items (stories often lack product_id directly)
  const storyProductMap = useMemo(() => {
    const intakeProductMap = {};
    items.forEach(item => {
      if (item.id && item.product_id) intakeProductMap[item.id] = item.product_id;
    });
    const map = {};
    stories.forEach(story => {
      const pid = story.product_id || intakeProductMap[story.intake_id] || null;
      if (pid) map[story.id] = pid;
    });
    return map;
  }, [stories, items]);

  // Helper: does this story belong to the selected product?
  const storyBelongsToProduct = (story, productId) => {
    if (!productId || productId === 'all') return true;
    return storyProductMap[story.id] === productId;
  };

  // Find current/previous sprint based on dates
  const getRelativeSprints = (sprintList) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sorted = [...sprintList].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    const current = sorted.find(s => {
      const start = new Date(s.start_date);
      const end = new Date(s.end_date);
      return today >= start && today <= end;
    });
    const past = sorted.filter(s => new Date(s.end_date) < today);
    const previous = past.length > 0 ? past[past.length - 1] : null;
    return { current, previous };
  };

  // Filter sprints: find sprints containing stories for the selected product
  const filteredSprints = useMemo(() => {
    if (selectedProduct === 'all') return sprints;
    // Find sprint IDs that contain stories for this product (via intake mapping)
    const sprintIdsForProduct = new Set(
      stories
        .filter(s => s.sprint_id && storyBelongsToProduct(s, selectedProduct))
        .map(s => s.sprint_id)
    );
    return sprints.filter(s =>
      s.product_id === selectedProduct || sprintIdsForProduct.has(s.id)
    );
  }, [sprints, stories, selectedProduct, storyProductMap]);

  // Build sprint dropdown options
  const sprintOptions = useMemo(() => {
    if (selectedProduct === 'all') {
      // Portfolio mode: show Previous/Current sprint instead of sprint numbers
      const { current, previous } = getRelativeSprints(sprints);
      const opts = [{ value: 'all', label: 'All Sprints' }];
      if (previous) opts.push({ value: previous.id, label: 'Previous Sprint (' + previous.name + ')' });
      if (current) opts.push({ value: current.id, label: 'Current Sprint (' + current.name + ')' });
      // Also show any remaining sprints
      sprints.forEach(s => {
        if (s.id !== current?.id && s.id !== previous?.id) {
          opts.push({ value: s.id, label: s.name + (s.product_name ? ' (' + s.product_name + ')' : '') });
        }
      });
      return opts;
    }
    // Product-specific: show that product's sprints
    const opts = [{ value: 'all', label: 'All Sprints' }];
    filteredSprints.forEach(s => {
      opts.push({ value: s.id, label: s.name + (s.product_name ? ' (' + s.product_name + ')' : '') });
    });
    return opts;
  }, [sprints, filteredSprints, selectedProduct]);

  // Calculate metrics based on filters
  const metrics = useMemo(() => {
    // Filter items by product
    let filteredItems = items;
    if (selectedProduct !== 'all') {
      const productName = products.find(p => p.id === selectedProduct)?.name;
      filteredItems = items.filter(i => i.product_id === selectedProduct || i.product === productName);
    }

    // Filter stories by sprint and/or product (using intake-based mapping)
    let filteredStories = stories;
    if (selectedSprint !== 'all' && selectedProduct !== 'all') {
      filteredStories = stories.filter(s =>
        s.sprint_id === selectedSprint && storyBelongsToProduct(s, selectedProduct)
      );
    } else if (selectedSprint !== 'all') {
      filteredStories = stories.filter(s => s.sprint_id === selectedSprint);
    } else if (selectedProduct !== 'all') {
      filteredStories = stories.filter(s => storyBelongsToProduct(s, selectedProduct));
    }
    
    // State distribution (from actual intake states)
    const byState = {};
    filteredItems.forEach(item => {
      const state = item.state || 'CAPTURED';
      byState[state] = (byState[state] || 0) + 1;
    });
    
    // Priority distribution (P0-P4)
    const byPriority = { P0: 0, P1: 0, P2: 0, P3: 0, P4: 0, 'Unset': 0 };
    filteredItems.forEach(item => {
      const priority = item.urgency || item.priority;
      if (priority && byPriority.hasOwnProperty(priority)) {
        byPriority[priority]++;
      } else {
        byPriority['Unset']++;
      }
    });
    
    // Type distribution
    const byType = {
      Epic: filteredItems.filter(i => i.type === 'epic').length,
      Feature: filteredItems.filter(i => i.type === 'feature' || i.type === 'pbi').length,
      Story: filteredStories.length
    };
    
    // Story points
    const totalStoryPoints = filteredStories.reduce((sum, s) => sum + (s.story_points || 0), 0);
    const completedStoryPoints = filteredStories.filter(s => s.state === 'DONE').reduce((sum, s) => sum + (s.story_points || 0), 0);
    
    // RICE scores (stored on stories, not intake items)
    const storiesWithRice = filteredStories.filter(s => s.rice_score);
    const avgRiceScore = storiesWithRice.length > 0
      ? storiesWithRice.reduce((sum, s) => sum + s.rice_score, 0) / storiesWithRice.length
      : 0;
    
    // Sprint metrics (for selected sprint or current sprint)
    const targetSprint = selectedSprint !== 'all' 
      ? sprints.find(s => s.id === selectedSprint)
      : filteredSprints.find(s => s.state === 'active') || filteredSprints[0];
    
    let sprintStories = targetSprint
      ? stories.filter(s => s.sprint_id === targetSprint.id)
      : [];
    // Apply product filter to sprint stories too
    if (selectedProduct !== 'all' && sprintStories.length > 0) {
      sprintStories = sprintStories.filter(s => storyBelongsToProduct(s, selectedProduct));
    }
    
    const sprintMetrics = targetSprint ? {
      id: targetSprint.id,
      name: targetSprint.name,
      product_name: targetSprint.product_name,
      totalStories: sprintStories.length,
      completedStories: sprintStories.filter(s => s.state === 'DONE').length,
      totalPoints: sprintStories.reduce((sum, s) => sum + (s.story_points || 0), 0),
      completedPoints: sprintStories.filter(s => s.state === 'DONE').reduce((sum, s) => sum + (s.story_points || 0), 0),
      inProgressPoints: sprintStories.filter(s => s.state === 'IN_PROGRESS').reduce((sum, s) => sum + (s.story_points || 0), 0)
    } : null;
    
    // Velocity (last 3 sprints for selected product)
    const completedSprints = filteredSprints
      .filter(s => s.state === 'completed' || s.state === 'done')
      .slice(0, 3);
    const velocityData = completedSprints.map(sprint => {
      const sprintStories = stories.filter(s => s.sprint_id === sprint.id && s.state === 'DONE');
      return {
        name: sprint.name,
        points: sprintStories.reduce((sum, s) => sum + (s.story_points || 0), 0)
      };
    });
    const avgVelocity = velocityData.length > 0 
      ? velocityData.reduce((sum, v) => sum + v.points, 0) / velocityData.length 
      : 0;
    
    // Product sizes - show all products, using intake-based mapping
    const productSizes = products.map(product => {
      const productItems = items.filter(i => i.product_id === product.id || i.product === product.name);
      const productStories = stories.filter(s => storyProductMap[s.id] === product.id);
      return {
        id: product.id,
        name: product.name,
        itemCount: productItems.length,
        storyCount: productStories.length,
        storyPoints: productStories.reduce((sum, s) => sum + (s.story_points || 0), 0),
        total: productItems.length + productStories.length
      };
    }).sort((a, b) => b.total - a.total);
    
    // Story state distribution
    const storyByState = {};
    filteredStories.forEach(story => {
      const state = story.state || 'TODO';
      storyByState[state] = (storyByState[state] || 0) + 1;
    });
    
    return {
      totalItems: filteredItems.length,
      totalStories: filteredStories.length,
      byState,
      storyByState,
      byPriority,
      byType,
      totalStoryPoints,
      completedStoryPoints,
      avgRiceScore,
      scoredStoryCount: storiesWithRice.length,
      sprintMetrics,
      velocityData,
      avgVelocity,
      productSizes,
      targetSprint
    };
  }, [items, stories, sprints, products, selectedProduct, selectedSprint, filteredSprints, storyProductMap]);

  // Load sprint health and burndown when sprint changes
  useEffect(() => {
    if (metrics?.sprintMetrics?.id) {
      loadSprintHealth(metrics.sprintMetrics.id);
      loadBurndown(metrics.sprintMetrics.id);
    } else {
      setBurndownData(null);
    }
  }, [metrics?.sprintMetrics?.id, selectedProduct]);

  // Load flow metrics when filters change
  useEffect(() => {
    if (!loading) {
      loadFlowMetrics();
    }
  }, [selectedProduct, selectedSprint, loading]);

  // Lazy-load PR metrics when PR tab is selected
  useEffect(() => {
    if (controlChartTab === 'pr' && !prMetrics && !loadingPr) {
      loadPrMetrics();
    }
  }, [controlChartTab]);

  const loadSprintHealth = async (sprintId) => {
    setLoadingHealth(true);
    try {
      const res = await api.post(`/ai/sprint-health/${sprintId}`);
      setSprintHealth(res.data);
    } catch (err) {
      console.error('Failed to load sprint health:', err);
      setSprintHealth(null);
    } finally {
      setLoadingHealth(false);
    }
  };

  const loadBurndown = async (sprintId) => {
    setLoadingBurndown(true);
    try {
      const params = {};
      if (selectedProduct !== 'all') params.product_id = selectedProduct;
      const res = await api.get(`/sprints/${sprintId}/burndown`, { params });
      setBurndownData(res.data);
    } catch (err) {
      console.error('Failed to load burndown:', err);
      setBurndownData(null);
    } finally {
      setLoadingBurndown(false);
    }
  };

  const loadFlowMetrics = async () => {
    setLoadingFlow(true);
    try {
      const params = {};
      if (selectedProduct !== 'all') params.product_id = selectedProduct;
      if (selectedSprint !== 'all') params.sprint_id = selectedSprint;
      const res = await api.get('/dashboard/flow-metrics', { params });
      setFlowMetrics(res.data);
    } catch (err) {
      console.error('Failed to load flow metrics:', err);
      setFlowMetrics(null);
    } finally {
      setLoadingFlow(false);
    }
  };

  const loadPrMetrics = async () => {
    setLoadingPr(true);
    try {
      const res = await api.get('/devops/pull-requests/metrics');
      setPrMetrics(res.data);
    } catch (err) {
      console.error('Failed to load PR metrics:', err);
      setPrMetrics(null);
    } finally {
      setLoadingPr(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 lg:p-8 flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-2 border-[#98139C]/30 border-t-[#98139C] rounded-full animate-spin" />
      </div>
    );
  }

  // Calculate completion rate based on stories
  const completionRate = metrics?.totalStories > 0 
    ? Math.round((metrics.storyByState?.DONE || 0) / metrics.totalStories * 100) 
    : 0;

  const pointsCompletionRate = metrics?.totalStoryPoints > 0 
    ? Math.round((metrics.completedStoryPoints / metrics.totalStoryPoints) * 100) 
    : 0;

  return (
    <div className="p-4 lg:p-8" data-testid="metrics-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C]">Analytics</p>
          <h1 className="text-4xl font-heading text-[#30302F] dark:text-white mt-1" style={{ letterSpacing: '-0.8px' }}>Metrics Dashboard</h1>
          <p className="text-[#30302F] dark:text-white mt-2 font-light text-lg">Track velocity, health, and team performance</p>
        </div>
        
        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-[#30302F]/70 dark:text-white/70" />
            <CustomSelect
              value={selectedProduct}
              onChange={(e) => {
                setSelectedProduct(e.target.value);
                setSelectedSprint('all'); // Reset sprint when product changes
              }}
              options={[
                { value: 'all', label: 'All Products' },
                ...products.map(p => ({ value: p.id, label: p.name }))
              ]}
              data-testid="product-filter"
            />
          </div>
          <CustomSelect
            value={selectedSprint}
            onChange={(e) => setSelectedSprint(e.target.value)}
            options={sprintOptions}
            data-testid="sprint-filter"
          />
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#98139C]/10 flex items-center justify-center">
              <Layers size={20} className="text-[#98139C]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#30302F] dark:text-white">{metrics?.totalItems || 0}</p>
              <p className="text-xs text-[#30302F]/70 dark:text-white/70">Total Items</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-green-500">{metrics?.storyByState?.DONE || 0} done</span>
            <span className="text-[#30302F]/40 dark:text-white/40">•</span>
            <span className="text-amber-500">{metrics?.storyByState?.IN_PROGRESS || 0} in progress</span>
            <span className="text-[#30302F]/40 dark:text-white/40">•</span>
            <span className="text-blue-500">{metrics?.totalStories || 0} stories</span>
          </div>
        </div>

        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <Target size={20} className="text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#30302F] dark:text-white">{completionRate}%</p>
              <p className="text-xs text-[#30302F]/70 dark:text-white/70">Completion Rate</p>
            </div>
          </div>
          <div className="w-full bg-gray-200 dark:bg-white/10 rounded-full h-2">
            <div className="bg-green-500 h-2 rounded-full transition-all" style={{ width: `${completionRate}%` }} />
          </div>
        </div>

        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Zap size={20} className="text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#30302F] dark:text-white">{metrics?.avgVelocity?.toFixed(1) || 0}</p>
              <p className="text-xs text-[#30302F]/70 dark:text-white/70">Avg Velocity (pts/sprint)</p>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-[#30302F]/70 dark:text-white/70">
            <TrendingUp size={12} className="text-green-500" />
            Based on last 3 sprints
          </div>
        </div>

        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <BarChart3 size={20} className="text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#30302F] dark:text-white">{metrics?.avgRiceScore?.toFixed(1) || 0}</p>
              <p className="text-xs text-[#30302F]/70 dark:text-white/70">Avg RICE Score</p>
            </div>
          </div>
          <div className="text-xs text-[#30302F]/70 dark:text-white/70">
            {metrics?.totalStories > 0 ? Math.round((metrics?.scoredStoryCount || 0) / metrics.totalStories * 100) : 0}% scored ({metrics?.scoredStoryCount || 0} stories)
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* State Distribution */}
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6 rounded-lg">
          <h3 className="text-sm font-bold text-[#30302F] dark:text-white mb-4 flex items-center gap-2">
            <PieChart size={16} className="text-[#98139C]" />
            State Distribution
          </h3>
          <div className="space-y-3">
            {Object.entries(metrics?.byState || {})
              .sort((a, b) => b[1] - a[1]) // Sort by count descending
              .map(([state, count]) => {
                const percentage = metrics?.totalItems > 0 ? Math.round((count / metrics.totalItems) * 100) : 0;
                const config = stateConfig[state] || { label: state.replace(/_/g, ' '), color: 'bg-gray-400' };
                return (
                  <div key={state} className="flex items-center gap-3">
                    <span className="w-28 text-xs text-[#30302F]/80 dark:text-white/80 truncate" title={config.label}>{config.label}</span>
                    <div className="flex-1 bg-gray-200 dark:bg-white/10 rounded-full h-3">
                      <div className={`${config.color} h-3 rounded-full transition-all`} style={{ width: `${percentage}%` }} />
                    </div>
                    <span className="w-10 text-right text-xs font-medium text-[#30302F] dark:text-white">{count}</span>
                    <span className="w-10 text-right text-xs text-[#30302F]/60 dark:text-white/60">{percentage}%</span>
                  </div>
                );
              })}
            {Object.keys(metrics?.byState || {}).length === 0 && (
              <p className="text-sm text-[#30302F]/60 dark:text-white/60 text-center py-4">No items to display</p>
            )}
          </div>
        </div>

        {/* Priority Distribution */}
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6 rounded-lg">
          <h3 className="text-sm font-bold text-[#30302F] dark:text-white mb-4 flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-500" />
            Priority Distribution
          </h3>
          <div className="space-y-3">
            {Object.entries(metrics?.byPriority || {})
              .filter(([_, count]) => count > 0) // Only show priorities with items
              .map(([priority, count]) => {
                const percentage = metrics?.totalItems > 0 ? Math.round((count / metrics.totalItems) * 100) : 0;
                const config = priorityConfig[priority] || { label: priority, color: 'bg-gray-400', badge: 'bg-gray-100 text-gray-600' };
                return (
                  <div key={priority} className="flex items-center gap-3">
                    <Badge className={`w-14 justify-center text-[11px] ${config.badge}`}>{priority}</Badge>
                    <div className="flex-1 bg-gray-200 dark:bg-white/10 rounded-full h-3">
                      <div className={`${config.color} h-3 rounded-full transition-all`} style={{ width: `${percentage}%` }} />
                    </div>
                    <span className="w-10 text-right text-xs font-medium text-[#30302F] dark:text-white">{count}</span>
                    <span className="w-10 text-right text-xs text-[#30302F]/60 dark:text-white/60">{percentage}%</span>
                  </div>
                );
              })}
            {Object.values(metrics?.byPriority || {}).every(v => v === 0) && (
              <p className="text-sm text-[#30302F]/60 dark:text-white/60 text-center py-4">No priorities set</p>
            )}
          </div>
        </div>
      </div>

      {/* Sprint Health Section */}
      <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6 rounded-lg mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-[#30302F] dark:text-white flex items-center gap-2">
            <Activity size={16} className="text-cyan-500" />
            Sprint Health Analysis
            {metrics?.sprintMetrics && (
              <span className="font-normal text-[#30302F]/70 dark:text-white/70">
                — {metrics.sprintMetrics.name} {metrics.sprintMetrics.product_name && `(${metrics.sprintMetrics.product_name})`}
              </span>
            )}
          </h3>
        </div>
        
        {loadingHealth ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-[#98139C]/30 border-t-[#98139C] rounded-full animate-spin" />
          </div>
        ) : sprintHealth?.analysis ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="text-center p-4 bg-[#faf8fb] dark:bg-white/5 rounded-lg">
              <p className={`text-3xl font-bold ${
                sprintHealth.analysis.health_score >= 70 ? 'text-green-500' :
                sprintHealth.analysis.health_score >= 40 ? 'text-amber-500' :
                'text-red-500'
              }`}>{sprintHealth.analysis.health_score}</p>
              <p className="text-xs text-[#30302F]/70 dark:text-white/70">Health Score</p>
            </div>
            <div className="text-center p-4 bg-[#faf8fb] dark:bg-white/5 rounded-lg">
              <p className={`text-3xl font-bold ${
                sprintHealth.analysis.completion_probability >= 70 ? 'text-green-500' :
                sprintHealth.analysis.completion_probability >= 40 ? 'text-amber-500' :
                'text-red-500'
              }`}>{sprintHealth.analysis.completion_probability}%</p>
              <p className="text-xs text-[#30302F]/70 dark:text-white/70">Completion Probability</p>
            </div>
            <div className="text-center p-4 bg-[#faf8fb] dark:bg-white/5 rounded-lg">
              <p className="text-3xl font-bold text-[#30302F] dark:text-white">{sprintHealth.analysis.days_remaining}</p>
              <p className="text-xs text-[#30302F]/70 dark:text-white/70">Days Remaining</p>
            </div>
            <div className="text-center p-4 bg-[#faf8fb] dark:bg-white/5 rounded-lg">
              <Badge className={`text-xs ${
                sprintHealth.analysis.status === 'healthy' ? 'bg-green-100 text-green-700' :
                sprintHealth.analysis.status === 'at_risk' ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700'
              }`}>{sprintHealth.analysis.status?.toUpperCase()}</Badge>
              <p className="text-xs text-[#30302F]/70 dark:text-white/70 mt-1">Status</p>
            </div>
          </div>
        ) : metrics?.sprintMetrics ? (
          <p className="text-center text-[#30302F]/70 dark:text-white/70 py-4">Loading sprint health analysis...</p>
        ) : (
          <p className="text-center text-[#30302F]/70 dark:text-white/70 py-4">Select a sprint to see health analysis</p>
        )}

        {sprintHealth?.analysis?.summary && (
          <div className="p-4 bg-[#faf8fb] dark:bg-white/5 rounded-lg">
            <p className="text-sm text-[#30302F]/80 dark:text-white/80">{sprintHealth.analysis.summary}</p>
          </div>
        )}

        {sprintHealth?.analysis?.recommendations?.length > 0 && (
          <div className="mt-4">
            <h4 className="text-xs font-bold uppercase tracking-wide text-[#30302F]/70 dark:text-white/70 mb-2">Recommendations</h4>
            <div className="space-y-2">
              {sprintHealth.analysis.recommendations.slice(0, 3).map((rec, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm">
                  <Badge className={`text-[10px] flex-shrink-0 ${
                    rec.priority === 'high' ? 'bg-red-100 text-red-700' :
                    rec.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>{rec.priority}</Badge>
                  <span className="text-[#30302F]/80 dark:text-white/80">{rec.action}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Team Bandwidth Section */}
      {metrics?.sprintMetrics?.id && (
        <div className="mb-8">
          <TeamBandwidth sprintId={metrics.sprintMetrics.id} compact={false} selectedProduct={selectedProduct} />
        </div>
      )}

      {/* Sprint Burndown Chart */}
      {metrics?.sprintMetrics && (
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6 rounded-lg mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-[#30302F] dark:text-white flex items-center gap-2">
              <TrendingDown size={16} className="text-[#98139C]" />
              Sprint Burndown — {metrics.sprintMetrics.name}
              {burndownData && (
                <span className="font-normal text-[#30302F]/60 dark:text-white/60">
                  {burndownData.done_points}/{burndownData.total_points} pts
                </span>
              )}
            </h3>
          </div>

          {loadingBurndown ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-[#98139C]/30 border-t-[#98139C] rounded-full animate-spin" />
            </div>
          ) : burndownData?.days?.length > 0 ? (
            <>
              {(() => {
                const isDark = document.documentElement.classList.contains('dark');
                const textColor = isDark ? '#ccc' : '#30302F';
                const gridColor = isDark ? '#333' : '#e3e3e3';
                const tooltipBg = isDark ? '#1a1a2e' : '#fff';
                return (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={burndownData.days} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: textColor }}
                        tickFormatter={(val) => {
                          const d = new Date(val + 'T00:00:00');
                          return `${d.toLocaleString('en-US', { month: 'short' })} ${d.getDate()}`;
                        }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: textColor }}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: tooltipBg,
                          border: '1px solid ' + gridColor,
                          borderRadius: 4,
                          fontSize: 12,
                          color: textColor,
                        }}
                        labelFormatter={(val) => {
                          const d = new Date(val + 'T00:00:00');
                          return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                        }}
                        formatter={(value, name) => [value != null ? `${value} pts` : '—', name === 'ideal' ? 'Ideal' : 'Actual']}
                      />
                      <Line
                        type="monotone"
                        dataKey="ideal"
                        stroke={isDark ? '#555' : '#d4d4d4'}
                        strokeDasharray="6 4"
                        strokeWidth={2}
                        dot={false}
                        name="ideal"
                      />
                      <Line
                        type="monotone"
                        dataKey="actual"
                        stroke="#98139C"
                        strokeWidth={2.5}
                        dot={{ fill: '#98139C', r: 3 }}
                        activeDot={{ r: 5 }}
                        connectNulls={false}
                        name="actual"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                );
              })()}
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-sm text-[#30302F]/60 dark:text-white/60">No burndown data available</p>
            </div>
          )}

          {/* Metric Cards */}
          <div className="grid grid-cols-4 gap-3 mt-4">
            <div className="text-center p-3 bg-green-50 dark:bg-green-900/10 rounded-lg">
              <p className="text-xl font-bold text-green-600 dark:text-green-400">{metrics.sprintMetrics.completedPoints}</p>
              <p className="text-[11px] text-green-700 dark:text-green-300">Done pts</p>
            </div>
            <div className="text-center p-3 bg-[#f0dcf0] dark:bg-[#98139C]/10 rounded-lg">
              <p className="text-xl font-bold text-[#98139C]">{metrics.sprintMetrics.inProgressPoints}</p>
              <p className="text-[11px] text-[#98139C]">In Progress pts</p>
            </div>
            <div className="text-center p-3 bg-amber-50 dark:bg-amber-900/10 rounded-lg">
              <p className="text-xl font-bold text-amber-600 dark:text-amber-400">
                {metrics.sprintMetrics.totalPoints - metrics.sprintMetrics.completedPoints}
              </p>
              <p className="text-[11px] text-amber-700 dark:text-amber-300">Remaining pts</p>
            </div>
            <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
              <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{metrics.sprintMetrics.totalPoints}</p>
              <p className="text-[11px] text-blue-700 dark:text-blue-300">Total pts</p>
            </div>
          </div>

          {/* Story Count */}
          <div className="flex items-center justify-between text-sm border-t border-[#e3e3e3] dark:border-white/10 pt-3 mt-4">
            <span className="text-[#30302F]/80 dark:text-white/80">Stories</span>
            <span className="font-medium text-[#30302F] dark:text-white">
              {metrics.sprintMetrics.completedStories} / {metrics.sprintMetrics.totalStories} completed
            </span>
          </div>
        </div>
      )}

      {/* Flow Efficiency — Control Chart */}
      <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6 rounded-lg mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-[#30302F] dark:text-white flex items-center gap-2">
            <Clock size={16} className="text-[#98139C]" />
            Flow Efficiency — Control Chart
          </h3>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 border-b border-[#e3e3e3] dark:border-white/10">
          {[
            { key: 'cycle', label: 'Cycle Time' },
            { key: 'lead', label: 'Lead Time' },
            { key: 'pr', label: 'PR Lead Time' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setControlChartTab(tab.key)}
              className={`px-4 py-2 text-xs font-bold transition-colors ${
                controlChartTab === tab.key
                  ? 'text-[#98139C] border-b-2 border-[#98139C] -mb-px'
                  : 'text-[#30302F]/70 dark:text-white/70 hover:text-[#98139C]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Chart content */}
        {(() => {
          const isLoading = controlChartTab === 'pr' ? loadingPr : loadingFlow;
          const chartData =
            controlChartTab === 'cycle'
              ? (flowMetrics?.cycle_time || [])
              : controlChartTab === 'lead'
              ? (flowMetrics?.lead_time || [])
              : (prMetrics?.data || []);
          const stats =
            controlChartTab === 'cycle'
              ? flowMetrics?.cycle_time_stats
              : controlChartTab === 'lead'
              ? flowMetrics?.lead_time_stats
              : null;

          // For PR tab, compute stats client-side
          const prStats = controlChartTab === 'pr' && prMetrics?.data?.length
            ? (() => {
                const vals = prMetrics.data.map(d => d.pr_lead_time_days).filter(v => v != null).sort((a, b) => a - b);
                if (!vals.length) return { median: 0, p85: 0, avg: 0, count: 0 };
                const mid = Math.floor(vals.length / 2);
                const median = vals.length % 2 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2;
                const p85Idx = Math.min(Math.floor(vals.length * 0.85), vals.length - 1);
                return {
                  median: Math.round(median * 100) / 100,
                  p85: Math.round(vals[p85Idx] * 100) / 100,
                  avg: Math.round(vals.reduce((a, b) => a + b, 0) / vals.length * 100) / 100,
                  count: vals.length,
                };
              })()
            : null;

          const activeStats = controlChartTab === 'pr' ? prStats : stats;

          // Build scatter data with numeric timestamps
          const scatterData = chartData.map((d) => {
            const dateField = controlChartTab === 'pr' ? d.closed_date : d.completed_date;
            const daysField = controlChartTab === 'pr' ? d.pr_lead_time_days : d.days;
            return {
              x: dateField ? new Date(dateField).getTime() : 0,
              y: daysField,
              title: d.title,
              points: d.story_points,
              author: d.created_by,
              repo: d.repository_name,
            };
          }).filter(d => d.x > 0 && d.y !== null && d.y !== undefined && d.y >= 0);

          if (isLoading) {
            return (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-[#98139C]/30 border-t-[#98139C] rounded-full animate-spin" />
              </div>
            );
          }

          if (!scatterData.length) {
            return (
              <div className="text-center py-12">
                <Clock size={32} className="mx-auto mb-2 text-[#30302F]/30 dark:text-white/30" />
                <p className="text-sm text-[#30302F]/60 dark:text-white/60">
                  {controlChartTab === 'pr'
                    ? 'No PR data. Sync pull requests from ADO Settings → Repositories.'
                    : 'No completed stories found for the selected filters.'}
                </p>
              </div>
            );
          }

          const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
          const textColor = isDark ? '#ccc' : '#30302F';
          const gridColor = isDark ? '#333' : '#e3e3e3';
          const tooltipBg = isDark ? '#1a1a2e' : '#fff';

          return (
            <>
              <ResponsiveContainer width="100%" height={320}>
                <ScatterChart margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis
                    type="number"
                    dataKey="x"
                    domain={['dataMin', 'dataMax']}
                    tick={{ fontSize: 11, fill: textColor }}
                    tickFormatter={(val) => {
                      const d = new Date(val);
                      return `${d.toLocaleString('en-US', { month: 'short' })} ${d.getDate()}`;
                    }}
                    name="Date"
                  />
                  <YAxis
                    type="number"
                    dataKey="y"
                    scale="log"
                    domain={['auto', 'auto']}
                    tick={{ fontSize: 11, fill: textColor }}
                    allowDecimals={false}
                    name="Days"
                    label={{ value: 'Days', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: textColor } }}
                  />
                  <ZAxis range={[40, 40]} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div style={{ backgroundColor: tooltipBg, border: `1px solid ${gridColor}`, borderRadius: 4, padding: '8px 12px', fontSize: 12, color: textColor }}>
                          <p className="font-medium mb-1" style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</p>
                          <p>{d.y.toFixed(1)} days</p>
                          {d.points != null && <p>{d.points} pts</p>}
                          {d.repo && <p>{d.repo}</p>}
                          {d.author && <p>{d.author}</p>}
                          <p className="text-[10px] mt-1 opacity-60">{new Date(d.x).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                        </div>
                      );
                    }}
                  />
                  {activeStats?.median > 0 && (
                    <ReferenceLine y={activeStats.median} stroke="#10b981" strokeDasharray="6 4" strokeWidth={1.5} label={{ value: `Median: ${activeStats.median}d`, position: 'right', fill: '#10b981', fontSize: 10 }} />
                  )}
                  {activeStats?.p85 > 0 && (
                    <ReferenceLine y={activeStats.p85} stroke="#ef4444" strokeDasharray="6 4" strokeWidth={1.5} label={{ value: `P85: ${activeStats.p85}d`, position: 'right', fill: '#ef4444', fontSize: 10 }} />
                  )}
                  <Scatter data={scatterData} fill="#98139C" />
                </ScatterChart>
              </ResponsiveContainer>

              {/* Stat cards */}
              {activeStats && (
                <div className="grid grid-cols-4 gap-3 mt-4">
                  <div className="text-center p-3 bg-green-50 dark:bg-green-900/10 rounded-lg">
                    <p className="text-xl font-bold text-green-600 dark:text-green-400">{activeStats.median ?? '—'}</p>
                    <p className="text-[11px] text-green-700 dark:text-green-300">Median (days)</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 dark:bg-red-900/10 rounded-lg">
                    <p className="text-xl font-bold text-red-600 dark:text-red-400">{activeStats.p85 ?? '—'}</p>
                    <p className="text-[11px] text-red-700 dark:text-red-300">85th Pctl (days)</p>
                  </div>
                  <div className="text-center p-3 bg-[#f0dcf0] dark:bg-[#98139C]/10 rounded-lg">
                    <p className="text-xl font-bold text-[#98139C]">{activeStats.avg ?? '—'}</p>
                    <p className="text-[11px] text-[#98139C]">Average (days)</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
                    <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{activeStats.count}</p>
                    <p className="text-[11px] text-blue-700 dark:text-blue-300">Data Points</p>
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

        {/* Product Size Distribution */}
        {metrics?.productSizes?.length > 0 && (
          <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6 rounded-lg">
            <h3 className="text-sm font-bold text-[#30302F] dark:text-white mb-4 flex items-center gap-2">
              <PieChart size={16} className="text-amber-500" />
              Product Size Distribution
            </h3>
            
            {/* Visual Pie-like representation */}
            <div className="flex items-center gap-6 mb-4">
              <div className="relative w-32 h-32">
                {(() => {
                  const total = metrics.productSizes.reduce((sum, p) => sum + p.total, 0);
                  let cumulative = 0;
                  const colors = ['#98139C', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
                  
                  if (total === 0) {
                    return (
                      <svg viewBox="0 0 36 36" className="w-full h-full">
                        <circle cx="18" cy="18" r="15.915" fill="transparent" stroke="#e5e7eb" strokeWidth="3.5" />
                        <text x="18" y="18" textAnchor="middle" dy=".3em" className="text-[6px] font-bold fill-current text-[#30302F] dark:text-white">0</text>
                      </svg>
                    );
                  }
                  
                  return (
                    <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
                      {metrics.productSizes.filter(p => p.total > 0).map((product, idx) => {
                        const percentage = (product.total / total) * 100;
                        const strokeDasharray = `${percentage} ${100 - percentage}`;
                        const strokeDashoffset = -cumulative;
                        cumulative += percentage;
                        return (
                          <circle
                            key={product.id}
                            cx="18"
                            cy="18"
                            r="15.915"
                            fill="transparent"
                            stroke={colors[idx % colors.length]}
                            strokeWidth="3.5"
                            strokeDasharray={strokeDasharray}
                            strokeDashoffset={strokeDashoffset}
                          />
                        );
                      })}
                      <text x="18" y="18" textAnchor="middle" dy=".3em" className="text-[6px] font-bold fill-current text-[#30302F] dark:text-white" transform="rotate(90, 18, 18)">
                        {total}
                      </text>
                    </svg>
                  );
                })()}
              </div>
              
              <div className="flex-1 space-y-2">
                {metrics.productSizes.map((product, idx) => {
                  const colors = ['bg-[#98139C]', 'bg-[#0ea5e9]', 'bg-[#10b981]', 'bg-[#f59e0b]', 'bg-[#ef4444]', 'bg-[#8b5cf6]'];
                  const total = metrics.productSizes.reduce((sum, p) => sum + p.total, 0);
                  const percentage = total > 0 ? Math.round((product.total / total) * 100) : 0;
                  return (
                    <div key={product.id} className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-sm ${product.total > 0 ? colors[idx % colors.length] : 'bg-gray-200 dark:bg-white/10'}`} />
                      <span className="flex-1 text-xs text-[#30302F] dark:text-white truncate" title={product.name}>
                        {product.name}
                      </span>
                      <span className="text-xs font-medium text-[#30302F] dark:text-white">{product.total}</span>
                      <span className="text-[11px] text-[#30302F]/60 dark:text-white/60 w-8 text-right">{percentage}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Story Points Summary */}
            <div className="border-t border-[#e3e3e3] dark:border-white/10 pt-4">
              <p className="text-xs text-[#30302F]/80 dark:text-white/80 mb-2">Story Points by Product</p>
              <div className="space-y-2">
                {metrics.productSizes.map((product, idx) => {
                  const maxPoints = Math.max(...metrics.productSizes.map(p => p.storyPoints), 1);
                  const width = (product.storyPoints / maxPoints) * 100;
                  return (
                    <div key={product.id} className="flex items-center gap-2">
                      <span className="w-24 text-[11px] text-[#30302F]/80 dark:text-white/80 truncate">{product.name}</span>
                      <div className="flex-1 bg-gray-200 dark:bg-white/10 rounded-full h-2">
                        <div className="bg-[#98139C] h-2 rounded-full transition-all" style={{ width: `${width}%` }} />
                      </div>
                      <span className="text-[11px] font-medium text-[#30302F] dark:text-white w-10 text-right">{product.storyPoints} pts</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
